/**
 * UI 组件注册表（T04）。
 *
 * 需求硬性要求：页面上每个「可以看到」的部件都要进 UI 组件清单，并记录位置，
 * 以便移植安卓。因此每个可见组件在挂载时调用 `useComponentMeta()` 登记元数据，
 * 生成脚本再据此产出清单。
 *
 * 本模块不依赖浏览器全局以外的任何环境，可在真实页面、jsdom 单测与
 * `npm run manifest` 的静态采集路径中复用。
 */
import { useEffect, useRef, useSyncExternalStore } from 'react';
import type { RefObject } from 'react';

import type { ComponentMeta } from '../core/types';
import { measureElement, readCanvasOrigin, readCanvasScale } from './measure';
import { sameAnchor } from './meta';
import { markSkinTarget } from './skinTarget';

export interface RegisteredComponent {
  meta: ComponentMeta;
  /** 挂到真实 DOM 的节点，未挂载时为 null。 */
  element: HTMLElement | null;
}

const components = new Map<string, RegisteredComponent>();
const listeners = new Set<() => void>();

let snapshot: ComponentMeta[] = [];

function cloneMeta(meta: ComponentMeta): ComponentMeta {
  return {
    ...meta,
    anchor: { ...meta.anchor },
    ...(meta.props === undefined ? {} : { props: [...meta.props] }),
  };
}

/** 稳定序列化，用于判断「同 id 再次登记」是不是同一份定义。 */
export function metaSignature(meta: ComponentMeta): string {
  return JSON.stringify({
    id: meta.id,
    page: meta.page,
    role: meta.role,
    label: meta.label,
    anchor: meta.anchor,
    zIndex: meta.zIndex,
    props: meta.props ?? [],
    notes: meta.notes ?? '',
  });
}

function recompute(): void {
  snapshot = [...components.values()].map((entry) => cloneMeta(entry.meta));
}

function emit(): void {
  recompute();
  for (const listener of [...listeners]) listener();
}

/**
 * 登记一个组件。幂等：同 id 覆盖旧定义并告警。
 * 覆盖时保留已绑定的 DOM 节点，避免热更新丢引用。
 */
export function registerComponent(meta: ComponentMeta): void {
  if (meta.id.trim() === '') {
    throw new Error('[ui-registry] 组件元数据缺少 id');
  }

  const existing = components.get(meta.id);
  if (existing !== undefined) {
    console.warn(`[ui-registry] 组件 id 重复，已覆盖：${meta.id}`);
  }

  components.set(meta.id, {
    meta: cloneMeta(meta),
    element: existing?.element ?? null,
  });
  emit();
}

/** 绑定 / 解绑 DOM 节点（由 `useComponentMeta` 内部调用）。 */
export function attachElement(id: string, element: HTMLElement | null): void {
  const existing = components.get(id);
  if (existing === undefined || existing.element === element) return;
  components.set(id, { ...existing, element });
  emit();
}

/** 读出全部组件（按登记顺序），返回深拷贝，调用方改不动注册表。 */
export function getAllComponents(): ComponentMeta[] {
  return snapshot.map((meta) => cloneMeta(meta));
}

/** 读取某个组件已绑定的 DOM 节点。 */
export function getComponentElement(id: string): HTMLElement | null {
  return components.get(id)?.element ?? null;
}

/** 读取某个组件的已登记元数据。 */
export function getRegisteredComponent(id: string): RegisteredComponent | null {
  const entry = components.get(id);
  return entry === undefined ? null : { meta: cloneMeta(entry.meta), element: entry.element };
}

/** 清空注册表（测试用）。 */
export function clearRegistry(): void {
  components.clear();
  emit();
}

/** 订阅注册表变化，返回取消订阅函数。 */
export function subscribeRegistry(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** React 侧订阅入口：注册表变化时触发重渲染。 */
export function useRegistry(): ComponentMeta[] {
  return useSyncExternalStore(
    subscribeRegistry,
    () => snapshot,
    () => snapshot,
  );
}

/**
 * 把组件元数据登记进全局注册表，并返回 ref 供挂到真实 DOM。
 *
 * 泛型只是为了让 `ref` 能直接挂到 `<button>` / `<div>` 等具体元素上；
 * 不传泛型时就是 `RefObject<HTMLElement>`。
 */
export function useComponentMeta<T extends HTMLElement = HTMLElement>(
  meta: ComponentMeta,
): RefObject<T> {
  const ref = useRef<T>(null);
  const latestMeta = useRef(meta);
  latestMeta.current = meta;

  const signature = metaSignature(meta);

  useEffect(() => {
    const current = latestMeta.current;
    const existing = components.get(current.id);

    // 只在「首次登记」或「定义真的变了」时覆盖，避免 StrictMode 双挂载刷告警。
    if (existing === undefined || metaSignature(existing.meta) !== signature) {
      registerComponent(current);
    }
    attachElement(current.id, ref.current);
    markSkinTarget(ref.current, current);

    return () => {
      attachElement(current.id, null);
    };
  }, [meta.id, signature]);

  return ref;
}

/**
 * 组件元数据 + 开发态测量（T04 的采集入口）。
 *
 * 行为：
 * 1. 先用页面声明的逻辑坐标（`meta.anchor`）登记，保证清单随时可用；
 * 2. 若传入 `frameRef` 且在开发态（`import.meta.env.DEV`），挂载后读取真实 DOM，
 *    按画布 scale 换算回逻辑坐标，坐标真的变了才覆盖登记（避免无谓告警）。
 *
 * 生产构建下 `import.meta.env.DEV` 为 false，只做第 1 步，无测量开销。
 */
export function useMeasuredComponentMeta<T extends HTMLElement = HTMLElement>(
  meta: ComponentMeta,
  frameRef?: RefObject<HTMLElement | null>,
): RefObject<T> {
  const ref = useComponentMeta<T>(meta);
  const latestMeta = useRef(meta);
  latestMeta.current = meta;

  useEffect(() => {
    if (import.meta.env.DEV !== true) return;

    const element = ref.current;
    const frame = frameRef?.current ?? null;
    const measured = measureElement(element, {
      scale: readCanvasScale(frame),
      origin: readCanvasOrigin(frame),
    });

    const current = latestMeta.current;
    if (measured === null || sameAnchor(measured, current.anchor)) return;

    registerComponent({ ...current, anchor: measured });
  }, [meta.id, frameRef, ref]);

  return ref;
}
